// ═══════════════════════════════════════════════════════════════════════════════
// BriefingPreviewDrawer — render ONE briefing against a chosen patient
// ═══════════════════════════════════════════════════════════════════════════════
//
// PURPOSE
//   Debug drawer for the Studio Briefings-list "Preview" button. Shows the
//   briefing composed against the chosen patient — totals strip + per-element
//   cards + the combined prompt text the LLM would see.
//
// SHARED FROM DAY ONE
//   Sibling of ElementPreviewDrawer. Host passes a transport-bound
//   `loadPreview(briefingId, memberId) => Promise<BriefingPreviewResult>` so
//   both Agent Studio (Tenant API) and the patient/AGUI app (AGUI API) can
//   reuse the same drawer.
//
// SHAPE NOTES
//   The backend returns BriefingPreviewResult with `briefings.length === 1`
//   for a single-briefing preview — the same shape returned by the agent-
//   level preview. We render the one briefing inline.
// ═══════════════════════════════════════════════════════════════════════════════

import { useCallback, useEffect, useState } from 'react';
import { Button, Drawer, Tag, Tooltip, message } from 'antd';
import {
  CloseOutlined,
  CopyOutlined,
  DownloadOutlined,
  FileTextOutlined,
  ReloadOutlined,
  UserOutlined,
} from '@ant-design/icons';
import { PatientPicker } from './PatientPicker';
import type {
  BriefingPreviewBriefing,
  BriefingPreviewElement,
  BriefingPreviewResult,
  BriefingPreviewStatus,
  MemberSearchHit,
  PatientHandle,
} from './types';

export interface BriefingPreviewDrawerProps {
  open: boolean;
  onClose: () => void;

  /** Briefing to preview. */
  briefing: {
    id: number;
    code: string;
    name?: string;
    version: number;
    isDraft?: boolean;
    isActive?: boolean;
  };

  /** Session patient — pre-fills the drawer's patient pill. */
  patient?: PatientHandle;

  /** Render the briefing against memberId. Host owns the transport. */
  loadPreview: (briefingId: number, memberId: number) => Promise<BriefingPreviewResult>;

  /** Member search backing the Change-patient popover. */
  searchPatients?: (query: string) => Promise<MemberSearchHit[]>;

  /** False in the patient app (patient locked). Defaults to true. */
  allowChangePatient?: boolean;
}

type Stage = 'needs-patient' | 'rendering' | 'loaded' | 'error';

export function BriefingPreviewDrawer(props: BriefingPreviewDrawerProps) {
  const { open, onClose, briefing, patient, loadPreview, searchPatients } = props;
  const allowChangePatient = props.allowChangePatient ?? true;

  const [stage, setStage]               = useState<Stage>('needs-patient');
  const [errorMsg, setErrorMsg]         = useState<string | null>(null);
  const [result, setResult]             = useState<BriefingPreviewResult | null>(null);
  const [activePatient, setActivePatient] = useState<PatientHandle | undefined>(patient);
  const [pickerOpen, setPickerOpen]     = useState(false);

  // Reset + auto-render on open.
  useEffect(() => {
    if (!open) return;
    setErrorMsg(null);
    setResult(null);
    setPickerOpen(false);
    setActivePatient(patient);
    setStage(patient ? 'rendering' : 'needs-patient');
  }, [open, patient]);

  // Fire the load on stage flip.
  useEffect(() => {
    if (stage !== 'rendering' || !activePatient) return;
    let cancelled = false;
    (async () => {
      try {
        const data = await loadPreview(briefing.id, activePatient.memberId);
        if (cancelled) return;
        setResult(data);
        setStage('loaded');
      } catch (e: unknown) {
        if (cancelled) return;
        setErrorMsg(e instanceof Error ? e.message : 'Preview failed.');
        setStage('error');
      }
    })();
    return () => { cancelled = true; };
  }, [stage, activePatient, briefing.id, loadPreview]);

  const handlePick = useCallback((hit: MemberSearchHit) => {
    const label = [hit.firstName, hit.lastName].filter(Boolean).join(' ') || `Member #${hit.id}`;
    const meta  = formatMemberMeta(hit);
    setActivePatient({ memberId: hit.id, label, meta });
    setPickerOpen(false);
    setStage('rendering');
  }, []);

  const handleRerender = useCallback(() => {
    if (!activePatient) return;
    setStage('rendering');
  }, [activePatient]);

  const handleCopyCombined = useCallback(async () => {
    if (!result?.combinedPromptText) return;
    try {
      await navigator.clipboard.writeText(result.combinedPromptText);
      message.success('Copied combined prompt');
    } catch {
      message.error('Copy failed');
    }
  }, [result]);

  const handleExportJson = useCallback(() => {
    if (!result) return;
    try {
      const blob = new Blob([JSON.stringify(result, null, 2)], { type: 'application/json' });
      const url  = URL.createObjectURL(blob);
      const a    = document.createElement('a');
      a.href     = url;
      a.download = `briefing-preview-${briefing.code}-${activePatient?.memberId ?? 'unknown'}.json`;
      a.click();
      URL.revokeObjectURL(url);
    } catch {
      message.error('Export failed');
    }
  }, [result, briefing.code, activePatient]);

  const previewedBriefing: BriefingPreviewBriefing | undefined =
    result?.briefings?.[0];

  return (
    <Drawer
      open={open}
      onClose={onClose}
      placement="right"
      width={880}
      closable={false}
      title={null}
      styles={{
        body: { padding: 0, display: 'flex', flexDirection: 'column' },
        header: { display: 'none' },
      }}
    >
      {/* DEBUG banner */}
      <div style={debugBannerStyle}>
        <span style={debugDotStyle} />
        DEBUG PREVIEW · uses real patient data · renders identically to the LLM
      </div>

      {/* Header */}
      <div style={{ padding: '18px 24px 14px', display: 'flex', alignItems: 'flex-start', gap: 14 }}>
        <div style={iconTileStyle}><FileTextOutlined /></div>
        <div style={{ flex: 1, minWidth: 0 }}>
          <h1 style={{ margin: 0, fontSize: 19, letterSpacing: '-0.01em', lineHeight: 1.2 }}>
            {briefing.name || briefing.code}
          </h1>
          <div style={{ marginTop: 5, display: 'flex', alignItems: 'center', gap: 8, flexWrap: 'wrap' }}>
            <span style={codeStyle}>{briefing.code}</span>
            <Tag style={{ ...tagStyle, ...verTagStyle }}>{`v${briefing.version}`}</Tag>
            {briefing.isDraft
              ? <Tag style={{ ...tagStyle, ...draftTagStyle }}>draft</Tag>
              : <Tag style={{ ...tagStyle, ...publishedTagStyle }}>published</Tag>}
            {briefing.isActive === false && <Tag style={{ ...tagStyle, ...inactiveTagStyle }}>inactive</Tag>}
          </div>
        </div>
        <Button type="text" icon={<CloseOutlined />} onClick={onClose} />
      </div>

      {/* Patient bar */}
      {activePatient && (
        <div style={ptBarStyle}>
          <div style={avatarStyle}>{patientInitials(activePatient)}</div>
          <div style={{ display: 'flex', flexDirection: 'column', lineHeight: 1.25, minWidth: 0 }}>
            <div style={{ fontWeight: 600, fontSize: 13, display: 'flex', alignItems: 'center', gap: 6 }}>
              <span>{activePatient.label || `Member #${activePatient.memberId}`}</span>
              <Tooltip title="Inherited from the patient currently selected in this app">
                <Tag style={fromSessionPillStyle}>↳ from session</Tag>
              </Tooltip>
            </div>
            {activePatient.meta && (
              <div style={{ color: '#94A3B8', fontSize: 11, fontFamily: 'JetBrains Mono, monospace' }}>
                {activePatient.meta}
              </div>
            )}
          </div>
          <div style={{ flex: 1 }} />
          {allowChangePatient && searchPatients && (
            <Button size="small" icon={<UserOutlined />} onClick={() => setPickerOpen((v) => !v)}>
              {pickerOpen ? 'Hide' : 'Change patient'}
            </Button>
          )}
        </div>
      )}

      {/* Expanded picker */}
      {(pickerOpen || stage === 'needs-patient') && searchPatients && (
        <div style={{ padding: '0 24px 4px', marginTop: 12 }}>
          <PatientPicker
            searchPatients={searchPatients}
            onPick={handlePick}
            currentMemberId={activePatient?.memberId}
          />
        </div>
      )}

      {/* Body */}
      <div style={bodyStyle}>
        {stage === 'needs-patient' && !pickerOpen && (
          <EmptyState
            hasPicker={Boolean(searchPatients)}
            onPickClick={() => setPickerOpen(true)}
          />
        )}

        {stage === 'rendering' && (
          <div style={{ textAlign: 'center', padding: 40, color: '#64748B', fontSize: 13 }}>
            Rendering briefing against patient…
          </div>
        )}

        {stage === 'error' && (
          <div style={errorBoxStyle}>
            {errorMsg || 'Preview failed.'}
            <div style={{ marginTop: 10 }}>
              <Button size="small" icon={<ReloadOutlined />} onClick={handleRerender} disabled={!activePatient}>
                Try again
              </Button>
            </div>
          </div>
        )}

        {stage === 'loaded' && result && (
          <>
            <TotalsStrip result={result} />

            <SectionLabel style={{ marginTop: 16 }}>
              Elements ({previewedBriefing?.elements?.length ?? 0})
            </SectionLabel>

            {(previewedBriefing?.elements?.length ?? 0) === 0 ? (
              <div style={{ padding: 18, color: '#64748B', fontSize: 13, fontStyle: 'italic' }}>
                This briefing has no elements in its composition.
              </div>
            ) : (
              previewedBriefing!.elements.map((el, i) => (
                <ElementCard key={`${el.elementCode}-${i}`} index={i + 1} element={el} />
              ))
            )}

            {/* Combined prompt */}
            <SectionLabel style={{ marginTop: 18 }}>
              Combined prompt · what the LLM sees
            </SectionLabel>
            <CombinedPromptBlock text={result.combinedPromptText} renderedCount={result.renderedCount} />
          </>
        )}
      </div>

      {/* Footer */}
      <div style={footerStyle}>
        <Button
          type="primary"
          icon={<ReloadOutlined />}
          onClick={handleRerender}
          disabled={!activePatient || stage === 'rendering'}
        >
          Re-render
        </Button>
        <Button
          icon={<CopyOutlined />}
          onClick={handleCopyCombined}
          disabled={stage !== 'loaded' || !result?.combinedPromptText}
        >
          Copy combined prompt
        </Button>
        <Button
          icon={<DownloadOutlined />}
          onClick={handleExportJson}
          disabled={stage !== 'loaded' || !result}
        >
          Export JSON
        </Button>
        <div style={{ flex: 1 }} />
        <Button type="text" onClick={onClose}>Close</Button>
      </div>
    </Drawer>
  );
}

// ────────────────────────────────────────────────────────────────────
// Sub-components
// ────────────────────────────────────────────────────────────────────

function TotalsStrip({ result }: { result: BriefingPreviewResult }) {
  return (
    <div style={totalsStripStyle}>
      <Stat label="Rendered" value={result.renderedCount} tone="green" />
      <Stat label="Empty"    value={result.emptyCount}    tone="gray" />
      <Stat label="Deduped"  value={result.dedupedCount}  tone="amber" />
      <Stat label="Fault"    value={result.faultCount + result.noProviderCount} tone="red" />
      <Stat label="Elapsed"  value={`${Math.round(result.elapsedMs)} ms`} tone="default" />
    </div>
  );
}

function Stat({ label, value, tone }: { label: string; value: number | string; tone: 'green' | 'gray' | 'amber' | 'red' | 'default' }) {
  const colorMap: Record<typeof tone, string> = {
    green:   '#064E3B',
    gray:    '#1F2937',
    amber:   '#78350F',
    red:     '#7F1D1D',
    default: '#0F172A',
  };
  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
      <span style={{ fontSize: 10, textTransform: 'uppercase', letterSpacing: '0.06em', fontWeight: 600, color: '#94A3B8' }}>{label}</span>
      <span style={{ fontSize: 18, fontWeight: 600, color: colorMap[tone], letterSpacing: '-0.01em' }}>{value}</span>
    </div>
  );
}

function ElementCard({ index, element }: { index: number; element: BriefingPreviewElement }) {
  const [open, setOpen] = useState(false);

  const isError = element.status === 'Fault' || element.status === 'NoProvider';
  const isEmpty = element.status === 'Empty';
  const isDedup = element.status === 'Deduped';

  return (
    <div style={{ ...cardStyle, ...(isError ? { borderColor: '#FCA5A5' } : {}) }}>
      <div
        style={{ display: 'flex', alignItems: 'center', gap: 10, padding: '12px 14px', cursor: 'pointer' }}
        onClick={() => setOpen((v) => !v)}
      >
        <span style={elementNumStyle}>{index}</span>
        <div style={{ flex: 1, minWidth: 0, display: 'flex', alignItems: 'center', gap: 8, flexWrap: 'wrap' }}>
          <span style={{ fontWeight: 600, fontSize: 13.5 }}>{element.displayName || element.elementCode}</span>
          <span style={{ fontFamily: 'JetBrains Mono, monospace', fontSize: 11, color: '#94A3B8' }}>{element.elementCode}</span>
        </div>
        <Tag style={tierTagInlineStyle(element.tier)}>{`T${element.tier}`}</Tag>
        <StatusPill status={element.status} />
        <span style={{ fontSize: 11, color: '#94A3B8', fontFamily: 'JetBrains Mono, monospace', minWidth: 50, textAlign: 'right' }}>
          {Math.round(element.elapsedMs)} ms
        </span>
        <span style={{ color: '#94A3B8', fontSize: 10, width: 16, textAlign: 'center', transform: open ? 'rotate(90deg)' : undefined, transition: 'transform 0.12s' }}>▸</span>
      </div>

      <div style={{ padding: '0 14px 12px' }}>
        <div style={{
          ...elementTextStyle,
          ...(isError ? elementTextErrorStyle : {}),
          ...(isEmpty ? elementTextEmptyStyle : {}),
          ...(isDedup ? elementTextDedupStyle : {}),
        }}>
          {isError && element.faultMessage
            ? `⚠ ${element.faultMessage}`
            : isEmpty
              ? (element.text || '(no data for this patient — element omitted from prompt)')
              : isDedup
                ? `⊘ Deduped — already supplied by '${element.dedupedFromBriefingCode}'`
                : (element.text || '(no text)')}
        </div>
      </div>

      {open && (
        <div style={{ borderTop: '1px dashed #E5E7EB', padding: '12px 14px', background: '#FAFBFD' }}>
          <DetailGrid>
            <DetailRow k="Renderer"  v={element.rendererClass || '—'} />
            <DetailRow k="Category"  v={element.category || '—'} />
            <DetailRow k="Max tokens" v={String(element.maxTokens)} />
            <DetailRow k="Status"    v={element.status} />
            {element.faultMessage && <DetailRow k="Fault" v={element.faultMessage} />}
            {element.dedupedFromBriefingCode && <DetailRow k="Deduped from" v={element.dedupedFromBriefingCode} />}
          </DetailGrid>
        </div>
      )}
    </div>
  );
}

function StatusPill({ status }: { status: BriefingPreviewStatus }) {
  const styles: Record<BriefingPreviewStatus, React.CSSProperties> = {
    Rendered:   { background: '#D1FAE5', color: '#064E3B', borderColor: '#6EE7B7' },
    Empty:      { background: '#F3F4F6', color: '#1F2937', borderColor: '#D1D5DB' },
    // Calm slate on purpose — Suppressed means "data on file, deliberately
    // not sent to the LLM"; reassurance, not a warning. Matches the
    // suppressed-row styling in AgentBriefingsPreviewDrawer.
    Suppressed: { background: '#F1F5F9', color: '#334155', borderColor: '#CBD5E1' },
    Deduped:    { background: '#FEF3C7', color: '#78350F', borderColor: '#FDE68A' },
    Fault:      { background: '#FEE2E2', color: '#7F1D1D', borderColor: '#FCA5A5' },
    NoProvider: { background: '#FEE2E2', color: '#7F1D1D', borderColor: '#FCA5A5' },
  };
  return <Tag style={{ ...statusPillStyle, ...styles[status] }}>{status.toLowerCase()}</Tag>;
}

function CombinedPromptBlock({ text, renderedCount }: { text?: string; renderedCount: number }) {
  if (!text || renderedCount === 0) {
    return (
      <div style={combinedEmptyStyle}>
        No element rendered non-empty text. The LLM would receive no patient-briefing block this turn.
      </div>
    );
  }
  return (
    <pre style={combinedBlockStyle}>{text}</pre>
  );
}

function EmptyState({ hasPicker, onPickClick }: { hasPicker: boolean; onPickClick: () => void }) {
  return (
    <div style={emptyStateStyle}>
      <div style={{ fontSize: 24, marginBottom: 6 }}><UserOutlined /></div>
      <div style={{ fontSize: 14, fontWeight: 500, color: '#0F172A', marginBottom: 4 }}>
        No patient selected
      </div>
      <div style={{ fontSize: 12, color: '#475569', marginBottom: 14, maxWidth: 360 }}>
        Pick a patient to render this briefing's elements and see the combined prompt the LLM would receive.
      </div>
      {hasPicker && (
        <Button type="primary" icon={<UserOutlined />} onClick={onPickClick}>
          Pick a patient
        </Button>
      )}
    </div>
  );
}

function DetailGrid({ children }: { children: React.ReactNode }) {
  return (
    <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '6px 18px', fontSize: 12 }}>
      {children}
    </div>
  );
}
function DetailRow({ k, v }: { k: string; v: string }) {
  return (
    <>
      <span style={{ color: '#94A3B8', fontSize: 10.5, textTransform: 'uppercase', letterSpacing: '0.04em', fontWeight: 600 }}>{k}</span>
      <span style={{ color: '#0F172A', fontFamily: 'JetBrains Mono, monospace', fontSize: 12 }}>{v}</span>
    </>
  );
}

function SectionLabel({ children, style }: { children: React.ReactNode; style?: React.CSSProperties }) {
  return (
    <div style={{ fontSize: 10.5, textTransform: 'uppercase', letterSpacing: '0.08em', color: '#94A3B8', fontWeight: 700, margin: '14px 0 10px', ...style }}>
      {children}
    </div>
  );
}

// ────────────────────────────────────────────────────────────────────
// Helpers (mirror ElementPreviewDrawer)
// ────────────────────────────────────────────────────────────────────

function patientInitials(p: PatientHandle): string {
  if (p.initials) return p.initials;
  const label = p.label || '';
  const parts = label.trim().split(/\s+/);
  if (parts.length >= 2) return (parts[0][0] + parts[1][0]).toUpperCase();
  if (label.length >= 2) return label.slice(0, 2).toUpperCase();
  return '?';
}

function formatMemberMeta(hit: MemberSearchHit): string {
  const age = hit.dob ? ageFromDob(hit.dob) : null;
  const sex = (hit.gender || '').trim().toLowerCase().startsWith('m') ? 'M' : (hit.gender || '').trim().toLowerCase().startsWith('f') ? 'F' : '';
  const parts: string[] = [];
  if (age != null) parts.push(`${age}${sex}`);
  if (hit.mrn) parts.push(`MRN ••••${hit.mrn.slice(-4)}`);
  if (hit.languageCode) parts.push(hit.languageCode);
  return parts.join(' · ');
}

function ageFromDob(dob: string): number | null {
  const d = new Date(dob);
  if (Number.isNaN(d.getTime())) return null;
  const today = new Date();
  let age = today.getFullYear() - d.getFullYear();
  const m = today.getMonth() - d.getMonth();
  if (m < 0 || (m === 0 && today.getDate() < d.getDate())) age--;
  return age >= 0 ? age : null;
}

// ────────────────────────────────────────────────────────────────────
// Styles
// ────────────────────────────────────────────────────────────────────

const debugBannerStyle: React.CSSProperties = {
  background: 'linear-gradient(90deg,#FEF3C7 0%, #FDE68A 100%)',
  color: '#78350F', fontSize: 11, fontWeight: 600, letterSpacing: '0.02em',
  padding: '7px 22px', display: 'flex', alignItems: 'center', gap: 8,
  borderBottom: '1px solid #FCD34D',
};
const debugDotStyle: React.CSSProperties = {
  width: 6, height: 6, borderRadius: '50%',
  background: '#D97706', boxShadow: '0 0 0 3px rgba(217,119,6,.18)',
};

const iconTileStyle: React.CSSProperties = {
  width: 36, height: 36, borderRadius: 10,
  background: '#DBEAFE', color: '#1E3A5F',
  display: 'inline-flex', alignItems: 'center', justifyContent: 'center',
  flexShrink: 0, fontWeight: 700, fontSize: 16,
};

const codeStyle: React.CSSProperties = {
  fontFamily: 'JetBrains Mono, monospace', fontSize: 11, color: '#94A3B8',
};

const tagStyle: React.CSSProperties = {
  margin: 0, fontSize: 10, fontWeight: 600, letterSpacing: '0.02em', textTransform: 'uppercase',
  padding: '1px 9px', borderRadius: 999, lineHeight: '18px',
};
const verTagStyle: React.CSSProperties        = { background: '#F3F4F6', color: '#1F2937', borderColor: '#D1D5DB', fontFamily: 'JetBrains Mono, monospace' };
const publishedTagStyle: React.CSSProperties  = { background: '#D1FAE5', color: '#064E3B', borderColor: '#6EE7B7' };
const draftTagStyle: React.CSSProperties      = { background: '#FEF3C7', color: '#78350F', borderColor: '#FDE68A' };
const inactiveTagStyle: React.CSSProperties   = { background: '#FEE2E2', color: '#7F1D1D', borderColor: '#FCA5A5' };

const ptBarStyle: React.CSSProperties = {
  margin: '0 24px', padding: '10px 14px', background: '#FAFBFD',
  border: '1px solid #E5E7EB', borderRadius: 10,
  display: 'flex', alignItems: 'center', gap: 10,
};
const avatarStyle: React.CSSProperties = {
  width: 30, height: 30, borderRadius: '50%',
  background: 'linear-gradient(135deg,#FB923C,#F97316)', color: '#fff',
  fontWeight: 600, fontSize: 12,
  display: 'inline-flex', alignItems: 'center', justifyContent: 'center',
  flexShrink: 0, boxShadow: '0 0 0 2px #FED7AA',
};
const fromSessionPillStyle: React.CSSProperties = {
  background: '#ECFEFF', color: '#155E75', borderColor: '#A5F3FC',
  margin: 0, fontSize: 9.5, fontWeight: 500, padding: '1px 7px', borderRadius: 999,
  textTransform: 'none', letterSpacing: 0,
};

const totalsStripStyle: React.CSSProperties = {
  margin: '14px 0 0', padding: '14px 16px',
  background: 'linear-gradient(180deg,#F0F7FF 0%,#F8FAFC 100%)',
  border: '1px solid #BFDBFE', borderRadius: 12,
  display: 'grid', gridTemplateColumns: 'repeat(5,1fr)', gap: 8,
};

const bodyStyle: React.CSSProperties = {
  flex: 1, overflow: 'auto', padding: '6px 24px 100px',
};

const cardStyle: React.CSSProperties = {
  background: '#fff', border: '1px solid #E5E7EB', borderRadius: 12,
  marginBottom: 10, overflow: 'hidden',
};

const elementNumStyle: React.CSSProperties = {
  width: 22, height: 22, borderRadius: 6,
  background: '#F3F4F6', color: '#1F2937',
  fontSize: 11, fontWeight: 600,
  display: 'inline-flex', alignItems: 'center', justifyContent: 'center',
  fontFamily: 'JetBrains Mono, monospace',
};

const tierTagInlineStyle = (tier: number): React.CSSProperties => ({
  ...tagStyle,
  background: tier === 1 ? '#DBEAFE' : '#EDE9FE',
  color:      tier === 1 ? '#1E3A5F' : '#4C1D95',
  borderColor: tier === 1 ? '#BFDBFE' : '#C4B5FD',
});

const statusPillStyle: React.CSSProperties = {
  ...tagStyle,
};

const elementTextStyle: React.CSSProperties = {
  fontFamily: 'JetBrains Mono, monospace', fontSize: 12.5, lineHeight: 1.55,
  background: '#F8FAFC', border: '1px solid #E5E7EB', borderRadius: 8,
  padding: '10px 12px', color: '#0F172A',
  whiteSpace: 'pre-wrap', wordBreak: 'break-word',
};
const elementTextErrorStyle: React.CSSProperties = { background: '#FEF2F2', borderColor: '#FCA5A5', color: '#7F1D1D' };
const elementTextEmptyStyle: React.CSSProperties = { color: '#94A3B8', fontStyle: 'italic' };
const elementTextDedupStyle: React.CSSProperties = { background: '#FFFBEB', borderColor: '#FDE68A', color: '#78350F' };

const combinedBlockStyle: React.CSSProperties = {
  margin: 0, padding: '16px 18px',
  background: '#0F172A', color: '#E2E8F0', borderRadius: 12,
  fontFamily: 'JetBrains Mono, monospace', fontSize: 12.5, lineHeight: 1.7,
  border: '1px solid #1E293B', overflow: 'auto',
  whiteSpace: 'pre-wrap', wordBreak: 'break-word',
};

const combinedEmptyStyle: React.CSSProperties = {
  padding: 14, fontSize: 12.5, color: '#64748B', fontStyle: 'italic',
  background: '#F8FAFC', border: '1px dashed #E5E7EB', borderRadius: 10,
};

const emptyStateStyle: React.CSSProperties = {
  padding: 28, border: '1px dashed #BFDBFE', background: '#F0F7FF',
  borderRadius: 12, textAlign: 'center', marginTop: 14,
  display: 'flex', flexDirection: 'column', alignItems: 'center',
};

const errorBoxStyle: React.CSSProperties = {
  padding: 14, background: '#FEF2F2', color: '#7F1D1D',
  border: '1px solid #FCA5A5', borderRadius: 10, fontSize: 13,
};

const footerStyle: React.CSSProperties = {
  position: 'sticky', bottom: 0, background: '#fff',
  borderTop: '1px solid #E5E7EB', padding: '12px 22px',
  display: 'flex', alignItems: 'center', gap: 8,
  boxShadow: '0 -6px 16px rgba(15,23,42,.04)',
};
